import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { RunIndicator } from "../components/RunIndicator";
import { StatTiles } from "../components/StatTiles";
import {
  buttonClasses,
  Card,
  Empty,
  ErrorNote,
  Spinner,
  StatusBadge,
  runTone,
} from "../components/primitives";
import { api } from "../lib/api";
import { count, humanise, money, relativeTime } from "../lib/format";
import { playbookTitle } from "../lib/labels";
import type { RunSummary } from "../lib/types";

/**
 * Every engagement, current and past.
 *
 * **"Engagement" here, "run" underneath.** The nav, the route and this file
 * say engagement because that is what the reader started; `run_id`, `/api/runs`
 * and `runs/` on disk keep the word the whole system is built on. They are not
 * quite the same thing — `core/projects/CLAUDE.md` draws the line: a *project*
 * is the booked engagement and a *run* is one execution of it — which is why
 * renaming the domain would have cost more than it bought.
 *
 * The tiles and the search follow the Projects page deliberately: two lists of
 * the firm's work should not be read two different ways.
 *
 * **Both are computed here, not fetched.** The list is already complete and
 * already scoped to the caller, so a `/summary` endpoint beside it would be a
 * second source for numbers the client can add up — and a second thing to
 * disagree.
 */
const STATUSES = ["running", "queued", "completed", "failed", "cancelled"];

export function Engagements() {
  const runs = useQuery({
    queryKey: ["runs"],
    queryFn: api.runs,
    // Cheap poll so a run started elsewhere (CLI, another tab) shows up.
    refetchInterval: 10_000,
  });
  const playbooks = useQuery({ queryKey: ["playbooks"], queryFn: api.playbooks });

  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");

  const all = useMemo(() => runs.data ?? [], [runs.data]);

  const stats = useMemo(() => {
    const by = (s: string) => all.filter((r) => r.status === s).length;
    return {
      total: all.length,
      running: by("running") + by("queued"),
      queued: by("queued"),
      completed: by("completed"),
      failed: by("failed"),
      cancelled: by("cancelled"),
      findings: all.reduce((n, r) => n + (r.findings ?? 0), 0),
      cost: all.reduce((n, r) => n + (r.cost_usd ?? 0), 0),
    };
  }, [all]);

  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return all.filter((run) => {
      if (status !== "all" && run.status !== status) return false;
      if (!needle) return true;
      // The run id too: it is what a person copies out of a support message
      // or a `cdd` command, and it is the one field that is always unique.
      return [
        run.target,
        run.playbook,
        playbookTitle(playbooks.data, run.playbook),
        run.sector,
        run.run_id,
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(needle));
    });
  }, [all, status, search, playbooks.data]);

  if (runs.isLoading) return <Spinner label="Loading engagements" />;
  if (runs.isError) return <ErrorNote error={runs.error} />;

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Engagements</h1>
        <Link to="/" className={buttonClasses("primary")}>
          New engagement
        </Link>
      </div>

      {/*
        Boxes again, after a spell as one line of facts (#124) that read as
        small print. Each filters the list to what it counts, and failed is
        its own number — "not completed" once folded eight cancellations into
        a red figure that read as eighteen failures.
      */}
      {all.length > 0 && (
        <StatTiles
          items={[
            {
              value: count(stats.total),
              label: stats.total === 1 ? "engagement" : "engagements",
              onClick: () => setStatus("all"),
              active: status === "all",
            },
            {
              value: count(stats.running),
              label: "running",
              hint: stats.queued ? `${count(stats.queued)} waiting for a slot` : undefined,
              tone: stats.running > 0 ? "warning" : undefined,
              onClick: () => setStatus("running"),
              active: status === "running",
            },
            {
              value: count(stats.completed),
              label: "completed",
              onClick: () => setStatus("completed"),
              active: status === "completed",
            },
            {
              value: count(stats.failed),
              label: "failed",
              tone: stats.failed > 0 ? "critical" : undefined,
              onClick: () => setStatus("failed"),
              active: status === "failed",
            },
            {
              value: count(stats.cancelled),
              label: "cancelled",
              onClick: () => setStatus("cancelled"),
              active: status === "cancelled",
            },
            { value: money(stats.cost), label: "spent" },
            { value: count(stats.findings), label: "findings" },
          ]}
        />
      )}

      {all.length === 0 ? (
        <div className="card">
          <Empty
            title="No engagements yet"
            hint={
              <>
                Start one from <Link to="/" className="text-accent underline">the
                start page</Link>, or from the terminal with{" "}
                <code className="rounded-sm bg-surface-2 px-1 py-0.5 font-mono text-xs">
                  cdd run "Target Co"
                </code>
                .
              </>
            }
          />
        </div>
      ) : (
        <Card>
          <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-2.5">
            <select
              className="field w-auto py-1.5"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="all">All statuses</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <input
              className="field w-auto flex-1 py-1.5"
              placeholder="Search target, playbook, sector or run id"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <span className="font-mono text-sm tabular-nums text-muted">
              {rows.length === all.length
                ? count(rows.length)
                : `${count(rows.length)} of ${count(all.length)}`}
            </span>
          </div>

          {rows.length === 0 ? (
            <Empty
              title="Nothing matches"
              hint="Change the status filter, or clear the search."
            />
          ) : (
            <>
            {/* Below md a row is a stacked item, not six columns: the table
                scrolled sideways on a phone with the target cut off (#124). */}
            <ul className="divide-y divide-line md:hidden">
              {rows.map((run) => (
                <StackedRow
                  key={run.run_id}
                  run={run}
                  playbook={playbookTitle(playbooks.data, run.playbook)}
                />
              ))}
            </ul>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-base">
                <thead>
                  <tr className="label border-b border-line text-left">
                    <th className="px-4 py-2.5 font-medium">Target</th>
                    <th className="px-4 py-2.5 font-medium">Status</th>
                    <th className="px-4 py-2.5 font-medium">Progress</th>
                    <th className="px-4 py-2.5 text-right font-medium">Findings</th>
                    <th className="px-4 py-2.5 text-right font-medium">Cost</th>
                    <th className="px-4 py-2.5 text-right font-medium">Started</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line">
                  {rows.map((run) => (
                    <Row
                      key={run.run_id}
                      run={run}
                      playbook={playbookTitle(playbooks.data, run.playbook)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </Card>
      )}
    </div>
  );
}

function hrefOf(run: RunSummary): string {
  return `/engagements/${encodeURIComponent(run.run_id)}${
    run.status === "completed" ? "?tab=deliverables" : ""
  }`;
}

function metaOf(run: RunSummary, playbook: string | null): string {
  return [playbook, run.sector, run.depth && `${run.depth} depth`].filter(Boolean).join(" · ");
}

const LIVE = new Set(["running", "queued"]);

/**
 * Where a run is. A bar only while it can move or where it stopped matters —
 * twenty-nine identical full bars on finished runs said nothing (#124).
 */
function Progress({ run }: { run: RunSummary }) {
  const fraction = `${run.phases_done}/${run.phases_total || "—"}`;
  if (run.status === "completed") {
    return <span className="font-mono text-sm tabular-nums text-muted">{fraction}</span>;
  }
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-24 rounded-sm bg-surface-2">
        <div
          className="h-full rounded-sm bg-accent"
          style={{
            width: `${run.phases_total ? (run.phases_done / run.phases_total) * 100 : 0}%`,
          }}
        />
      </div>
      <span className="text-sm text-ink-2">
        {run.current_phase ? humanise(run.current_phase) : (
          <span className="font-mono tabular-nums">{fraction}</span>
        )}
      </span>
    </div>
  );
}

function Row({ run, playbook }: { run: RunSummary; playbook: string | null }) {
  return (
    <tr className="group hover:bg-surface-2">
      <td className="px-4 py-3">
        <Link to={hrefOf(run)} className="block">
          <span className="flex items-center gap-2">
            <span className="font-medium group-hover:text-accent">
              {run.target ?? run.run_id}
            </span>
            {/* The status column says what state it is in; this says only
                that it is moving, so it appears only while it is (#124). */}
            {LIVE.has(run.status) && (
              <RunIndicator
                status={run.status}
                phase={run.current_phase ? humanise(run.current_phase) : null}
              />
            )}
          </span>
          <span className="mt-0.5 block text-sm text-muted">{metaOf(run, playbook)}</span>
        </Link>
      </td>
      <td className="px-4 py-3">
        <StatusBadge tone={runTone(run.status)}>{run.status}</StatusBadge>
      </td>
      <td className="px-4 py-3">
        <Progress run={run} />
      </td>
      <td className="px-4 py-3 text-right font-mono tabular-nums">{count(run.findings)}</td>
      <td className="px-4 py-3 text-right font-mono tabular-nums">{money(run.cost_usd)}</td>
      <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-muted">
        {relativeTime(run.started_at)}
      </td>
    </tr>
  );
}

function StackedRow({ run, playbook }: { run: RunSummary; playbook: string | null }) {
  return (
    <li>
      <Link to={hrefOf(run)} className="block px-4 py-3 hover:bg-surface-2">
        <span className="flex items-start justify-between gap-3">
          <span className="min-w-0">
            <span className="block truncate font-medium">{run.target ?? run.run_id}</span>
            <span className="mt-0.5 block text-sm text-muted">{metaOf(run, playbook)}</span>
          </span>
          <StatusBadge tone={runTone(run.status)}>{run.status}</StatusBadge>
        </span>
        <span className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink-2">
          <Progress run={run} />
          <span>
            <span className="font-mono tabular-nums text-ink">{count(run.findings)}</span>{" "}
            findings
          </span>
          <span className="font-mono tabular-nums text-ink">{money(run.cost_usd)}</span>
          <span className="font-mono tabular-nums text-muted">{relativeTime(run.started_at)}</span>
        </span>
      </Link>
    </li>
  );
}
